import { Notice } from 'ui';
import type React from 'react';
import { PageHead } from 'ui/kit/PageHead';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { clsx } from 'clsx';
import { useIsMobileLayout } from 'hooks/useMediaQuery';
import { useAuthSession } from 'hooks/useAuthSession';
import { useGetCurrentProfile } from 'modules/Profile/model/services';
import { FadeIn } from 'ui/kit/uiMotion';

import classes from './Chat.module.scss';
import screen from './ChatScreen.module.scss';
import { ChatEmptyState } from './components/ChatEmptyState';
import {
  ChatHeader,
  ChatInput,
  ChatMessages,
  ChatSidebar,
  ChatTaskBubble,
  LeaveReviewModal,
  ReviewWindowPanel,
  ShareContactsButton,
  SuggestedQuestions,
} from './components';
import type { ChatRoomStatus } from './api/routes';
import { useChatChannel } from './hooks/useChatChannels';
import { useChatRooms } from './hooks/useChatRooms';
import { useChatViewerRole } from './hooks/useChatViewerRole';
import { useStudentChatBulletins } from './hooks/useStudentChatBulletins';
import { useStudentBulletinTutors } from './hooks/useStudentBulletinTutors';
import type { StudentBulletinTutorResponse } from './api/routes';
import { useDeepLinkedChatRoom } from './hooks/useDeepLinkedChatRoom';
import { useGlobalPresence } from './store/GlobalPresenceContext';
import { useOpenChat } from './store/OpenChatContext';
import type { ChatRoom, ContactShareState } from './types';
import {
  useCloseBulletinForDealMutation,
  useDecideBulletinResponseMutation,
  useOpenChatFromResponseMutation,
  useShareContactsMutation,
} from './api/queries';
import { buildChatUrl } from 'utils/buildChatUrl';
import { parsePositiveInt } from 'utils/parsePositiveInt';
import { addNotification } from 'notifications';
import { CHAT_CLOSED_MESSAGE, CHAT_MODERATION_BLOCKED_MESSAGE } from './utils/formatChatMessagingError';

import { findAccessibleChatRoom } from './utils/isAccessibleChat';
import {
  canSubmitReview,
  resolveDealStage,
  shouldShowReviewCta,
} from './utils/dealProgress';
import { sortRelatedBulletins } from './utils/sortBulletins';
import { mapApiRoomToChatRoom } from './utils/mapApiRoomToChatRoom';
import {
  isContactShareMessageText,
  toContactShareState,
} from './utils/contactShareState';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';
import { DetailsMenuSlot } from 'modules/Details/DetailsMenu/DetailsMenu';

interface ChatStudentProps {
  bulletinIdFromRoute?: number;
}

const roomHasBulletin = (room: ChatRoom, bulletinId: number): boolean =>
  room.related_bulletins.some((bulletin) => bulletin.bulletin_id === bulletinId);

const ChatStudent: React.FC<ChatStudentProps> = ({ bulletinIdFromRoute }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const preSelectedChatId = searchParams.get('chatId');
  const preSelectedRoomId = searchParams.get('roomId');
  const preSelectedUserIdParam = searchParams.get('userId');
  const preSelectedBulletinId = searchParams.get('bulletinId');
  const openReviewParam = searchParams.get('openReview');
  const parsedUserId = parsePositiveInt(preSelectedUserIdParam);
  const parsedBulletinId = parsePositiveInt(preSelectedBulletinId);

  const { data: profileData, isLoading: profileLoading } = useGetCurrentProfile();
  const { data: authSession } = useAuthSession();
  const currentUser = profileData?.id_user || 0;

  const navigate = useNavigate();
  const role = useChatViewerRole();
  const hasDeepLink = Boolean(preSelectedChatId || preSelectedRoomId);

  const { data: deepLinkedApiRoom, isLoading: deepLinkLoading } = useDeepLinkedChatRoom(
    preSelectedChatId,
    hasDeepLink && !profileLoading,
  );
  const { isUserOnline } = useGlobalPresence();
  const { setOpenChat } = useOpenChat();

  const [chatStatus, setChatStatus] = useState<ChatRoomStatus>('active');
  const [archiveTabSynced, setArchiveTabSynced] = useState(false);

  useEffect(() => {
    if (!deepLinkedApiRoom || archiveTabSynced) {
      return;
    }
    if (!deepLinkedApiRoom.active) {
      setChatStatus('closed');
    }
    setArchiveTabSynced(true);
  }, [deepLinkedApiRoom, archiveTabSynced]);

  const { chatRooms: tabChatRooms, isLoading: tabChatRoomsLoading } = useChatRooms(
    currentUser || 0,
    chatStatus,
  );
  const chatRooms = useMemo(() => {
    const base = tabChatRooms;

    if (deepLinkedApiRoom) {
      const mapped = mapApiRoomToChatRoom(deepLinkedApiRoom, isUserOnline);
      const onThisTab =
        mapped != null &&
        (chatStatus === 'closed' ? mapped.active === false : mapped.active !== false);
      if (mapped && onThisTab) {
        const merged = new Map(base.map((room) => [room.id, room]));
        merged.set(mapped.id, mapped);
        return [...merged.values()];
      }
    }

    return base;
  }, [chatStatus, tabChatRooms, deepLinkedApiRoom, isUserOnline]);
  const chatRoomsLoading = tabChatRoomsLoading || (hasDeepLink && deepLinkLoading);

  const { threads: studentThreads, isLoading: studentBulletinsLoading } = useStudentChatBulletins(
    chatStatus,
    role === 'student',
  );

  // Вторая вкладка нужна, чтобы пустой фильтр не прятал «Активные»/«Архив».
  // Заявка старше 3 суток сама по себе архив не открывает: живой чат остается в «Активных».
  const otherChatStatus: ChatRoomStatus = chatStatus === 'closed' ? 'active' : 'closed';
  const { threads: otherStudentThreads, isLoading: otherStudentLoading } = useStudentChatBulletins(
    otherChatStatus,
    role === 'student',
  );
  const { chatRooms: otherTutorRooms, isLoading: otherTutorLoading } = useChatRooms(
    currentUser || 0,
    otherChatStatus,
    role === 'tutor' && Boolean(currentUser),
  );
  const [archiveAutoOpened, setArchiveAutoOpened] = useState(false);
  useEffect(() => {
    if (archiveAutoOpened || role !== 'student' || chatStatus !== 'active' || hasDeepLink) return;
    if (studentBulletinsLoading || otherStudentLoading) return;
    setArchiveAutoOpened(true);
    if (studentThreads.length === 0 && otherStudentThreads.length > 0) {
      setChatStatus('closed');
    }
  }, [archiveAutoOpened, role, chatStatus, hasDeepLink, studentBulletinsLoading, otherStudentLoading, studentThreads.length, otherStudentThreads.length]);

  const bulletinThreads = role === 'student' ? studentThreads : [];
  const bulletinsLoading = role === 'student' ? studentBulletinsLoading : false;

  const [selectedChatId, setSelectedChatId] = useState<string | null>(preSelectedChatId);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(preSelectedRoomId);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(parsedUserId);
  const [selectedBulletinId, setSelectedBulletinId] = useState<number | null>(
    bulletinIdFromRoute ?? parsedBulletinId,
  );

  const { tutors: bulletinTutors, isLoading: bulletinTutorsLoading } = useStudentBulletinTutors(
    selectedBulletinId,
    chatStatus,
    role === 'student' && selectedBulletinId != null,
  );
  const [localContactShare, setLocalContactShare] = useState<ContactShareState | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [tutorResponseStatus, setTutorResponseStatus] = useState<
    'all' | 'pending' | 'accepted' | 'rejected'
  >('all');
  const suppressBulletinUrlSyncRef = useRef(false);
  const openReviewHandledRef = useRef(false);
  const userDismissedSelectionRef = useRef(false);
  const badChatIdNotifiedRef = useRef<string | null>(null);
  const isMobile = useIsMobileLayout();
  const [mobilePanel, setMobilePanel] = useState<'list' | 'chat'>(() =>
    preSelectedChatId ? 'chat' : 'list',
  );
  const [reportOpen, setReportOpen] = useState(false);

  const studentPanel: 'bulletins' | 'tutors' =
    role === 'student' && selectedBulletinId != null ? 'tutors' : 'bulletins';

  /** Chats list path: stay on details-route when opened from /profile/details/:id/chats */
  const listBasePath = useMemo(() => {
    if (role === 'tutor') return '/teacher/chat';
    if (bulletinIdFromRoute) return `/profile/details/${bulletinIdFromRoute}/chats`;
    return '/profile/chats';
  }, [bulletinIdFromRoute, role]);

  const buildStudentListUrl = useCallback(
    (bulletinId?: number | null) => {
      if (bulletinIdFromRoute) {
        return `/profile/details/${bulletinIdFromRoute}/chats`;
      }
      if (bulletinId != null) {
        return `/profile/chats?bulletinId=${bulletinId}`;
      }
      return '/profile/chats';
    },
    [bulletinIdFromRoute],
  );

  const filteredChatRooms = useMemo(() => {
    if (role === 'tutor') {
      if (tutorResponseStatus === 'all') {
        return chatRooms;
      }
      return chatRooms.filter((room) =>
        room.related_bulletins.some(
          (bulletin) => (bulletin.status || '').toLowerCase() === tutorResponseStatus,
        ),
      );
    }
    if (selectedBulletinId == null) {
      return [];
    }
    return chatRooms.filter((room) => roomHasBulletin(room, selectedBulletinId));
  }, [chatRooms, selectedBulletinId, role, tutorResponseStatus]);

  const resolveBulletinId = useCallback(
    (room: ChatRoom | null | undefined): number | null => {
      if (!room) {
        return selectedBulletinId ?? bulletinIdFromRoute ?? null;
      }
      const bulletins = sortRelatedBulletins(room.related_bulletins);
      if (
        selectedBulletinId &&
        bulletins.some((bulletin) => bulletin.bulletin_id === selectedBulletinId)
      ) {
        return selectedBulletinId;
      }
      if (
        bulletinIdFromRoute &&
        bulletins.some((bulletin) => bulletin.bulletin_id === bulletinIdFromRoute)
      ) {
        return bulletinIdFromRoute;
      }
      return bulletins[0]?.bulletin_id ?? selectedBulletinId ?? bulletinIdFromRoute ?? null;
    },
    [bulletinIdFromRoute, selectedBulletinId],
  );

  useEffect(() => {
    if (!profileData || chatRoomsLoading || bulletinsLoading) {
      return;
    }

    if (preSelectedChatId || preSelectedRoomId) {
      return;
    }

    if (userDismissedSelectionRef.current) {
      return;
    }

    if (role === 'student') {
      if (suppressBulletinUrlSyncRef.current) {
        if (!preSelectedBulletinId && !bulletinIdFromRoute) {
          suppressBulletinUrlSyncRef.current = false;
        }
        return;
      }
      const initialBulletinId = bulletinIdFromRoute ?? parsedBulletinId;
      if (initialBulletinId != null) {
        setSelectedBulletinId(initialBulletinId);
      }
      return;
    }

    if (chatRooms.length > 0) {
      const nextRoom = chatRooms[0];
      setSelectedChatId(nextRoom.id);
      setSelectedRoomId(nextRoom.room_id);
      setSelectedUserId(nextRoom.other_user_id);
      const bulletinId = resolveBulletinId(nextRoom);
      if (bulletinId != null) {
        setSelectedBulletinId(bulletinId);
      }
    }
  }, [
    profileData,
    chatRoomsLoading,
    bulletinsLoading,
    preSelectedChatId,
    preSelectedRoomId,
    preSelectedBulletinId,
    parsedBulletinId,
    bulletinIdFromRoute,
    role,
    chatRooms,
    resolveBulletinId,
  ]);

  useEffect(() => {
    if (role !== 'student' || selectedBulletinId == null) {
      return;
    }

    if (chatRoomsLoading) {
      return;
    }

    const roomsForBulletin = chatRooms.filter((room) =>
      roomHasBulletin(room, selectedBulletinId),
    );

    if (selectedChatId) {
      const currentRoom = findAccessibleChatRoom(roomsForBulletin, {
        chatId: selectedChatId,
        roomId: selectedRoomId,
      });
      if (!currentRoom) {
        const inAllRooms = findAccessibleChatRoom(chatRooms, {
          chatId: selectedChatId,
          roomId: selectedRoomId,
        });
        if (inAllRooms) {
          return;
        }
        if (preSelectedChatId || preSelectedRoomId) {
          if (deepLinkLoading) {
            return;
          }
          if (
            preSelectedChatId &&
            deepLinkedApiRoom &&
            String(deepLinkedApiRoom.id) === preSelectedChatId
          ) {
            return;
          }
        }
        setSelectedChatId(null);
        setSelectedRoomId(null);
        setSelectedUserId(null);
        navigate(buildStudentListUrl(selectedBulletinId), { replace: true });
      }
    }
  }, [
    role,
    selectedBulletinId,
    chatRooms,
    chatRoomsLoading,
    selectedChatId,
    selectedRoomId,
    navigate,
    buildStudentListUrl,
    preSelectedChatId,
    preSelectedRoomId,
    deepLinkLoading,
    deepLinkedApiRoom,
  ]);

  const activeChatRoom = useMemo(
    () =>
      findAccessibleChatRoom(chatRooms, {
        chatId: selectedChatId,
        roomId: selectedRoomId,
      }),
    [chatRooms, selectedChatId, selectedRoomId],
  );

  const selectedChatRoom = activeChatRoom;

  const selectedChatIdNumber = activeChatRoom ? parseInt(activeChatRoom.id, 10) : null;

  const shareContactsMutation = useShareContactsMutation();
  const openFromResponseMutation = useOpenChatFromResponseMutation();
  const decideResponseMutation = useDecideBulletinResponseMutation(selectedChatIdNumber);
  const closeBulletinMutation = useCloseBulletinForDealMutation(selectedChatIdNumber);

  const {
    messages,
    sendMessage,
    sendBlockedReason,
    otherUserOnline,
    contactShare,
    isContactShareReady,
    isLoadingHistory,
    isConnected,
    markReadForChat,
  } = useChatChannel({
      currentUserId: currentUser.toString(),
      selectedUserId: selectedUserId,
      roomId: selectedRoomId,
      chatId: activeChatRoom ? parseInt(activeChatRoom.id, 10) : null,
      viewerRole: role,
    });

  const activeContactShare = useMemo(() => {
    if (!localContactShare) {
      return contactShare;
    }
    if (!contactShare) {
      return localContactShare;
    }
    // Prefer freshest query payload (review window fields) over optimistic local share.
    return { ...localContactShare, ...contactShare };
  }, [localContactShare, contactShare]);

  const activeBulletin = useMemo(() => {
    const bulletins = sortRelatedBulletins(selectedChatRoom?.related_bulletins ?? []);
    if (!bulletins.length) {
      return null;
    }
    if (selectedBulletinId) {
      return bulletins.find((bulletin) => bulletin.bulletin_id === selectedBulletinId) ?? bulletins[0];
    }
    return bulletins[0];
  }, [selectedChatRoom?.related_bulletins, selectedBulletinId]);

  const filteredMessages = activeChatRoom ? messages : [];

  // Fallback: system contact_share card in history proves contacts were shared
  // even if contact_share query is briefly stale/null. Do NOT invent can_leave_review.
  const contactsSharedFromMessages = useMemo(
    () =>
      filteredMessages.some(
        (message) =>
          message.message_types?.includes('contact_share') ||
          isContactShareMessageText(message.text),
      ),
    [filteredMessages],
  );

  const contactShareForReview = useMemo(() => {
    if (activeContactShare?.shared) {
      return activeContactShare;
    }
    if (!contactsSharedFromMessages) {
      return activeContactShare;
    }
    // Message proves `shared` only. Review eligibility must come from API fields  - 
    // never default can_leave_review to true (breaks openReview deep-link after review).
    return {
      eligible: activeContactShare?.eligible ?? true,
      viewer_role: role === 'tutor' || role === 'student' ? role : null,
      user_consented: activeContactShare?.user_consented ?? true,
      other_consented: activeContactShare?.other_consented ?? true,
      tutor_consented: activeContactShare?.tutor_consented ?? true,
      student_consented: activeContactShare?.student_consented ?? true,
      review_eligible_now: activeContactShare?.review_eligible_now,
      can_leave_review: activeContactShare?.can_leave_review,
      has_left_review: activeContactShare?.has_left_review ?? false,
      deal_auto_closed: activeContactShare?.deal_auto_closed ?? false,
      contacts_shared_at: activeContactShare?.contacts_shared_at ?? null,
      review_window_ends_at: activeContactShare?.review_window_ends_at ?? null,
      seconds_until_review_window: activeContactShare?.seconds_until_review_window ?? null,
      ...activeContactShare,
      shared: true,
    } satisfies ContactShareState;
  }, [activeContactShare, contactsSharedFromMessages, role]);

  const dealStage = useMemo(
    () => resolveDealStage(activeBulletin, contactShareForReview),
    [activeBulletin, contactShareForReview],
  );

  // Visible after contacts stage (including when review already left - then disabled).
  const showReviewCta = shouldShowReviewCta(role, contactShareForReview, dealStage);
  const canLeaveReviewNow = showReviewCta && canSubmitReview(contactShareForReview);

  /** Deep-link open uses API contact_share only - never message-fallback inventing eligibility. */
  const canOpenReviewFromDeepLink = useMemo(() => {
    if (role !== 'student') {
      return false;
    }
    if (!isContactShareReady || !contactShare) {
      return false;
    }
    if (contactShare.has_left_review) {
      return false;
    }
    if (contactShare.can_leave_review === false) {
      return false;
    }
    return canSubmitReview(contactShare) && shouldShowReviewCta(role, contactShare, dealStage);
  }, [role, isContactShareReady, contactShare, dealStage]);

  const clearOpenReviewParam = useCallback(() => {
    if (!searchParams.has('openReview')) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('openReview');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    openReviewHandledRef.current = false;
    setReviewModalOpen(false);
  }, [selectedChatId]);

  useEffect(() => {
    if (openReviewParam !== '1') {
      return;
    }
    // Wait until chat + contact_share are ready so we do not race-open the modal.
    if (chatRoomsLoading || deepLinkLoading) {
      return;
    }
    if (selectedChatId && isLoadingHistory) {
      return;
    }
    if (
      preSelectedChatId &&
      !selectedChatId &&
      !userDismissedSelectionRef.current
    ) {
      return;
    }
    // No selected chat after dismiss/bad link - clear param and stop.
    if (!selectedChatId) {
      if (!openReviewHandledRef.current) {
        openReviewHandledRef.current = true;
      }
      clearOpenReviewParam();
      return;
    }
    if (!isContactShareReady) {
      return;
    }
    if (openReviewHandledRef.current) {
      clearOpenReviewParam();
      return;
    }
    openReviewHandledRef.current = true;

    // Open only when API says review is allowed; always clear openReview after attempt.
    // CTA stays visible but disabled when has_left_review / !can_leave_review.
    if (canOpenReviewFromDeepLink) {
      setReviewModalOpen(true);
    }
    clearOpenReviewParam();
  }, [
    openReviewParam,
    canOpenReviewFromDeepLink,
    clearOpenReviewParam,
    chatRoomsLoading,
    deepLinkLoading,
    selectedChatId,
    isLoadingHistory,
    isContactShareReady,
    preSelectedChatId,
  ]);

  const chatOtherUserOnline = activeChatRoom ? otherUserOnline : false;
  const chatIsLoadingHistory = activeChatRoom ? isLoadingHistory : false;
  const hasMessages = filteredMessages.length > 0;

  const messagingDisabled = useMemo(() => {
    if (!activeChatRoom || !selectedUserId) {
      return true;
    }
    if (activeChatRoom.moderation_blocked || sendBlockedReason) {
      return true;
    }
    if (!activeChatRoom.active) {
      return true;
    }
    const status = (activeBulletin?.status || '').toLowerCase();
    if (status === 'rejected') {
      return true;
    }
    return dealStage === 'rejected';
  }, [activeChatRoom, selectedUserId, activeBulletin, dealStage, sendBlockedReason]);

  const handleSendMessage = async (text: string) => {
    if (activeChatRoom?.moderation_blocked) {
      addNotification(CHAT_MODERATION_BLOCKED_MESSAGE, 'info');
      return;
    }
    if (messagingDisabled) {
      addNotification(CHAT_CLOSED_MESSAGE, 'info');
      return;
    }

    const result = await sendMessage(text);
    if (result === 'failed') {
      addNotification('Не удалось отправить сообщение. Попробуйте еще раз.', 'error');
    }
  };

  const handleQuestionClick = (question: string) => {
    void handleSendMessage(question);
  };

  const handleChatSelect = (chatId: string, roomId: string, userId: number) => {
    const room = findAccessibleChatRoom(chatRooms, { chatId, roomId });
    if (!room) {
      return;
    }
    userDismissedSelectionRef.current = false;
    const bulletinId = resolveBulletinId(room);

    setSelectedChatId(chatId);
    setSelectedRoomId(roomId);
    setSelectedUserId(userId);
    setSelectedBulletinId(bulletinId);
    setLocalContactShare(null);

    markReadForChat();

    navigate(
      buildChatUrl({
        chatId,
        roomId,
        bulletinId,
        userId,
        role,
        basePath: listBasePath,
      }),
    );

    if (isMobile) {
      setMobilePanel('chat');
    }
  };

  const handleBulletinSelect = (bulletinId: number) => {
    setSelectedBulletinId(bulletinId);
    setSelectedChatId(null);
    setSelectedRoomId(null);
    setSelectedUserId(null);
    setLocalContactShare(null);

    if (role === 'tutor') {
      navigate(`/teacher/chat?bulletinId=${bulletinId}`);
    } else {
      navigate(buildStudentListUrl(bulletinId));
    }

    if (isMobile) {
      setMobilePanel('list');
    }
  };

  const handleBulletinBack = useCallback(() => {
    suppressBulletinUrlSyncRef.current = true;
    setSelectedBulletinId(null);
    setSelectedChatId(null);
    setSelectedRoomId(null);
    setSelectedUserId(null);
    setLocalContactShare(null);

    if (bulletinIdFromRoute) {
      // Details-route: leave chats for the task page (not bare /profile/chats)
      navigate(`/profile/details/task/${bulletinIdFromRoute}`);
    } else if (role === 'tutor') {
      navigate('/teacher/chat');
    } else {
      navigate('/profile/chats');
    }

    if (isMobile) {
      setMobilePanel('list');
    }
  }, [bulletinIdFromRoute, isMobile, navigate, role]);

  const handleChatStatusChange = (status: ChatRoomStatus) => {
    setChatStatus(status);
    setSelectedBulletinId(null);
    setSelectedChatId(null);
    setSelectedRoomId(null);
    setSelectedUserId(null);
    setLocalContactShare(null);

    // Tab switch is a filter sync, not a history step
    navigate(listBasePath, { replace: true });
  };

  const handleBackToList = () => {
    setMobilePanel('list');
    // chat - tutors (keep bulletin); do not wipe history with replace
    if (role === 'student' && selectedChatId) {
      setSelectedChatId(null);
      setSelectedRoomId(null);
      setSelectedUserId(null);
      navigate(buildStudentListUrl(selectedBulletinId));
      return;
    }

    if (role === 'student' && bulletinIdFromRoute) {
      navigate(`/profile/details/task/${bulletinIdFromRoute}`);
      return;
    }

    if (role === 'student' && selectedBulletinId != null) {
      handleBulletinBack();
      return;
    }

    // Tutor desktop: dismiss selection and stay on list (do not auto-reselect rooms[0]).
    userDismissedSelectionRef.current = true;
    setSelectedChatId(null);
    setSelectedRoomId(null);
    setSelectedUserId(null);
    navigate(listBasePath);
  };

  useEffect(() => {
    if (!isMobile) {
      return;
    }
    setMobilePanel(preSelectedChatId ? 'chat' : 'list');
  }, [isMobile, preSelectedChatId]);

  const handleTutorSelect = async (tutor: StudentBulletinTutorResponse) => {
    if (tutor.chat_id != null && tutor.room_id) {
      handleChatSelect(String(tutor.chat_id), tutor.room_id, tutor.tutor_id);
      return;
    }

    if (!tutor.bulletin_response_id) {
      return;
    }

    try {
      const opened = await openFromResponseMutation.mutateAsync(tutor.bulletin_response_id);
      setSelectedBulletinId(opened.bulletin_id);
      handleChatSelect(String(opened.chat_id), opened.room_id, tutor.tutor_id);
    } catch {
      // ошибка показывается глобальным обработчиком API
    }
  };

  const handleShareContacts = async () => {
    if (!activeChatRoom) return;
    const result = await shareContactsMutation.mutateAsync(parseInt(activeChatRoom.id, 10));
    setLocalContactShare(toContactShareState(result.contact_share));
  };

  const handleOpenReviewModal = useCallback(() => {
    if (!canLeaveReviewNow) {
      return;
    }
    setReviewModalOpen(true);
  }, [canLeaveReviewNow]);

  const handleCloseReviewModal = useCallback(() => {
    setReviewModalOpen(false);
    clearOpenReviewParam();
  }, [clearOpenReviewParam]);

  const handleReviewSuccess = useCallback(() => {
    setLocalContactShare((prev) =>
      prev
        ? {
            ...prev,
            has_left_review: true,
            can_leave_review: false,
          }
        : prev,
    );
  }, []);

  const handleAcceptResponse = async () => {
    if (!activeBulletin?.bulletin_response_id) return;
    await decideResponseMutation.mutateAsync({
      responseId: activeBulletin.bulletin_response_id,
      action: 'accept',
    });
    setLocalContactShare(null);
  };

  const handleRejectResponse = async () => {
    if (!activeBulletin?.bulletin_response_id) return;
    await decideResponseMutation.mutateAsync({
      responseId: activeBulletin.bulletin_response_id,
      action: 'reject',
    });
    setLocalContactShare(null);
  };

  const handleCloseBulletin = async () => {
    if (!activeBulletin?.bulletin_id) return;
    await closeBulletinMutation.mutateAsync(activeBulletin.bulletin_id);
    setLocalContactShare(null);
  };

  useEffect(() => {
    if (chatRoomsLoading) {
      return;
    }

    const canonical = findAccessibleChatRoom(chatRooms, {
      chatId: preSelectedChatId,
      roomId: preSelectedRoomId,
    });

    if (!canonical) {
      if (!preSelectedChatId && !preSelectedRoomId) {
        return;
      }
      if (hasDeepLink && deepLinkLoading) {
        return;
      }
      if (hasDeepLink && deepLinkedApiRoom) {
        const mapped = mapApiRoomToChatRoom(deepLinkedApiRoom, isUserOnline);
        if (mapped) {
          userDismissedSelectionRef.current = false;
          const bulletinId = resolveBulletinId(mapped);
          setSelectedChatId(mapped.id);
          setSelectedRoomId(mapped.room_id);
          setSelectedUserId(mapped.other_user_id);
          if (bulletinId != null) {
            setSelectedBulletinId(bulletinId);
          }
          navigate(
            buildChatUrl({
              chatId: mapped.id,
              roomId: mapped.room_id,
              bulletinId,
              userId: mapped.other_user_id,
              role,
              basePath: listBasePath,
            }),
            { replace: true },
          );
          return;
        }
      }

      // Bad / inaccessible chatId: toast + list, do not wipe history with replace
      // and do not auto-pick chatRooms[0].
      const badKey = `${preSelectedChatId ?? ''}|${preSelectedRoomId ?? ''}`;
      if (badChatIdNotifiedRef.current !== badKey) {
        badChatIdNotifiedRef.current = badKey;
        addNotification('Чат недоступен или не найден. Выберите чат из списка.', 'warning');
      }
      userDismissedSelectionRef.current = true;
      setSelectedChatId(null);
      setSelectedRoomId(null);
      setSelectedUserId(null);
      setMobilePanel('list');

      const next = new URLSearchParams(searchParams);
      next.delete('chatId');
      next.delete('roomId');
      next.delete('userId');
      next.delete('openReview');
      // Keep bulletinId for student list context when present.
      setSearchParams(next, { replace: false });
      return;
    }

    userDismissedSelectionRef.current = false;
    badChatIdNotifiedRef.current = null;

    const bulletinId = resolveBulletinId(canonical);
    const stateNeedsSync =
      canonical.id !== selectedChatId ||
      canonical.room_id !== selectedRoomId ||
      canonical.other_user_id !== selectedUserId ||
      (bulletinId != null && bulletinId !== selectedBulletinId);

    if (stateNeedsSync) {
      setSelectedChatId(canonical.id);
      setSelectedRoomId(canonical.room_id);
      setSelectedUserId(canonical.other_user_id);
      if (bulletinId != null) {
        setSelectedBulletinId(bulletinId);
      }
    }

    const urlNeedsFix =
      canonical.id !== preSelectedChatId ||
      canonical.room_id !== preSelectedRoomId ||
      (parsedUserId != null && parsedUserId !== canonical.other_user_id);

    if (urlNeedsFix) {
      navigate(
        buildChatUrl({
          chatId: canonical.id,
          roomId: canonical.room_id,
          bulletinId,
          userId: canonical.other_user_id,
          role,
          basePath: listBasePath,
        }),
        { replace: true },
      );
    }
  }, [
    chatRoomsLoading,
    chatRooms,
    preSelectedChatId,
    preSelectedRoomId,
    preSelectedUserIdParam,
    preSelectedBulletinId,
    selectedBulletinId,
    role,
    resolveBulletinId,
    navigate,
    hasDeepLink,
    deepLinkLoading,
    deepLinkedApiRoom,
    isUserOnline,
    listBasePath,
    buildStudentListUrl,
  ]);

  useEffect(() => {
    if (!preSelectedChatId || chatRoomsLoading || bulletinTutorsLoading) {
      return;
    }

    const room = findAccessibleChatRoom(chatRooms, {
      chatId: preSelectedChatId,
      roomId: preSelectedRoomId,
    });
    const tutor =
      role === 'student'
        ? bulletinTutors.find(
            (item) =>
              item.chat_id != null &&
              String(item.chat_id) === preSelectedChatId &&
              item.room_id != null,
          )
        : undefined;

    if (!room && !tutor) {
      return;
    }

    if (!preSelectedRoomId) {
      const roomId = room?.room_id ?? tutor?.room_id ?? null;
      if (roomId) {
        setSelectedRoomId(roomId);
      }
    }

    if (!preSelectedUserIdParam) {
      const userId = room?.other_user_id ?? tutor?.tutor_id ?? null;
      if (userId) {
        setSelectedUserId(userId);
      }
    }

    if (!preSelectedBulletinId && !bulletinIdFromRoute) {
      const bulletinId =
        resolveBulletinId(room) ??
        (selectedBulletinId != null ? selectedBulletinId : null);
      if (bulletinId != null) {
        setSelectedBulletinId(bulletinId);
      }
    }
  }, [
    preSelectedChatId,
    preSelectedRoomId,
    preSelectedUserIdParam,
    preSelectedBulletinId,
    bulletinIdFromRoute,
    chatRooms,
    chatRoomsLoading,
    bulletinTutors,
    bulletinTutorsLoading,
    role,
    resolveBulletinId,
    selectedBulletinId,
  ]);

  const resolveChatBulletinId = () =>
    selectedBulletinId ??
    sortRelatedBulletins(selectedChatRoom?.related_bulletins ?? [])[0]?.bulletin_id ??
    bulletinIdFromRoute ??
    activeBulletin?.bulletin_id ??
    null;

  const handleProfileClick = () => {
    const other = selectedChatRoom?.other_user;
    if (!other?.id_user) return;

    const bulletinId = resolveChatBulletinId();

    if (role === 'student') {
      if (!bulletinId) return;
      navigate(`/profile/details/${bulletinId}/rep/${other.id_user}`);
      return;
    }

    if (bulletinId) {
      navigate(`/teacher/search/${bulletinId}`);
    }
  };

  const rootClass = role === 'tutor' ? classes['root--tutor'] : classes['root--student'];
  const layoutClass = clsx(
    classes.root,
    rootClass,
    isMobile && mobilePanel === 'list' && classes['root--mobile-list'],
    isMobile && mobilePanel === 'chat' && classes['root--mobile-chat'],
  );

  const sidebar = (
    <ChatSidebar
      role={role}
      chatStatus={chatStatus}
      onChatStatusChange={handleChatStatusChange}
      bulletinThreads={bulletinThreads}
      bulletinsLoading={bulletinsLoading}
      selectedBulletinId={selectedBulletinId}
      onBulletinSelect={handleBulletinSelect}
      onBulletinBack={role === 'student' ? handleBulletinBack : undefined}
      bulletinBackLabel={
        bulletinIdFromRoute ? 'К задаче' : 'Мои заявки'
      }
      studentPanel={studentPanel}
      bulletinTutors={bulletinTutors}
      bulletinTutorsLoading={bulletinTutorsLoading}
      chatRooms={filteredChatRooms}
      filteredChatRooms={filteredChatRooms}
      currentUser={profileData ?? null}
      currentUserId={currentUser}
      chatRoomsLoading={chatRoomsLoading}
      selectedChatId={selectedChatId || undefined}
      onChatSelect={handleChatSelect}
      onTutorSelect={role === 'student' ? handleTutorSelect : undefined}
      tutorResponseStatus={tutorResponseStatus}
      onTutorResponseStatusChange={setTutorResponseStatus}
    />
  );

  if (profileLoading) {
    return (
      <div className={classes.container}>
        <div className={layoutClass}>
          <div className={classes['chat-main']}>
            <div className={classes['connection-status']}>
              <div className={classes['connection-loading']}>
                <BrandLoader center label='Подключение к чату...' />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const selectedTutor = bulletinTutors.find(
    (tutor) =>
      (tutor.chat_id != null && String(tutor.chat_id) === selectedChatId) ||
      tutor.tutor_id === selectedUserId,
  );
  const otherUser =
    selectedChatRoom?.other_user ??
    (selectedTutor
      ? {
          id: selectedTutor.tutor_id,
          id_user: selectedTutor.tutor_id,
          id_profile: selectedTutor.other_user.id_profile,
          profile_type: 'teacher' as const,
          name: selectedTutor.other_user.name,
          surname: selectedTutor.other_user.surname,
          patronymic: selectedTutor.other_user.patronymic,
          description: selectedTutor.other_user.role_text,
          status: 'available',
          online: selectedTutor.other_user.is_online,
          last_seen_at: selectedTutor.other_user.last_seen_at ?? null,
          deleted: selectedTutor.other_user.is_deleted,
          sex: 0,
          list_skills: [],
          rating: 0,
          geo_range: {},
          mode: 'online',
          experience: 0,
          date_create: new Date().toISOString(),
          avatar: selectedTutor.other_user.profile_photo_token
            ? `/get_image/${selectedTutor.other_user.profile_photo_token}`
            : undefined,
        }
      : null);
  const canOpenChat = Boolean(activeChatRoom && otherUser);

  useEffect(() => {
    if (!canOpenChat || selectedChatIdNumber == null || !otherUser) {
      setOpenChat(null);
      return;
    }
    const parts = [otherUser.surname, otherUser.name, otherUser.patronymic]
      .map((part) => (part || '').trim())
      .filter(Boolean);
    const label =
      parts.join(' ') ||
      otherUser.name ||
      (otherUser.id_user != null ? `Пользователь #${otherUser.id_user}` : 'Собеседник');
    setOpenChat({ chatId: selectedChatIdNumber, peerLabel: label });
    return () => setOpenChat(null);
  }, [canOpenChat, otherUser, selectedChatIdNumber, setOpenChat]);

  const chatBulletinId = resolveChatBulletinId();
  const showEmpty =
    !hasDeepLink &&
    (role === 'student'
      ? !bulletinsLoading &&
        !otherStudentLoading &&
        studentThreads.length === 0 &&
        otherStudentThreads.length === 0
      : !chatRoomsLoading &&
        !otherTutorLoading &&
        chatRooms.length === 0 &&
        otherTutorRooms.length === 0);
  const pageCrumb =
    role === 'tutor' ? 'Личный кабинет / Чаты и отклики' : 'Личный кабинет / Мои чаты';
  const pageHeading =
    role === 'tutor' && showEmpty
      ? 'Пока нет диалогов'
      : role === 'tutor'
        ? 'Чаты и отклики'
        : 'Мои чаты';

  return (
  <div className={classes.container}>
    <FadeIn>
    <div className={screen.page}>
      <PageHead crumb={pageCrumb} title={pageHeading} />
      <DetailsMenuSlot />
      {showEmpty ? (
        <ChatEmptyState role={role} />
      ) : (
    <div className={layoutClass}>
      {canOpenChat ? (
        <div className={`${classes['chat-main']} ${screen.dialog}`}>
          {/* Объединяем ChatHeader с DealProgressPanel */}
          <ChatHeader
            otherUser={otherUser!}
            otherUserOnline={chatOtherUserOnline}
            role={role}
            onProfileClick={handleProfileClick}
            bulletinId={chatBulletinId}
            showBackButton
            onBack={handleBackToList}
            // Props для DealProgressPanel
            bulletin={activeBulletin}
            contactShare={contactShareForReview}
            onAcceptResponse={handleAcceptResponse}
            onRejectResponse={handleRejectResponse}
            onCloseBulletin={handleCloseBulletin}
            onLeaveReview={handleOpenReviewModal}
            chatId={selectedChatIdNumber}
            hideReportButton
            reportOpen={reportOpen}
            onReportClose={() => setReportOpen(false)}
            disabled={
              decideResponseMutation.isPending ||
              closeBulletinMutation.isPending ||
              shareContactsMutation.isPending
            }
          />

          {/* Убираем отдельный DealProgressPanel */}
          
          <ShareContactsButton
            contactShare={contactShareForReview ?? activeContactShare}
            chatId={selectedChatIdNumber}
            userId={currentUser || null}
            initialPhone={authSession?.phone ?? ''}
            initialEmail={authSession?.email ?? ''}
            onConsent={handleShareContacts}
            onContactsUpdated={() => setLocalContactShare(null)}
            disabled={shareContactsMutation.isPending}
          />

          {showReviewCta ? (
            <ReviewWindowPanel
              contactShare={contactShareForReview}
              onLeaveReview={handleOpenReviewModal}
            />
          ) : null}

          {role === 'student' && otherUser?.id_user ? (
            <LeaveReviewModal
              open={reviewModalOpen}
              onClose={handleCloseReviewModal}
              tutorUserId={otherUser.id_user}
              chatId={selectedChatIdNumber}
              onSuccess={handleReviewSuccess}
            />
          ) : null}

          <div className={classes['chat-content']}>
            {chatIsLoadingHistory ? (
              <div className={classes['no-messages']}>
                <BrandLoader center label='Загрузка истории сообщений...' />
              </div>
            ) : (
              <>
                {!isConnected && (
                  <div className={classes['connection-status']}>
                    <p>Подключение к чату в реальном времени...</p>
                  </div>
                )}
                {!hasMessages && (
                  <ChatTaskBubble bulletinId={selectedBulletinId} role={role} />
                )}
                <ChatMessages
                  messages={filteredMessages}
                  currentUserId={currentUser}
                  isLoading={isLoadingHistory}
                />
              </>
            )}
            {!hasMessages && !chatIsLoadingHistory && !messagingDisabled && (
              <SuggestedQuestions role={role} onQuestionClick={handleQuestionClick} />
            )}
          </div>

          {activeChatRoom?.moderation_blocked ? (
            <Notice
              collapsible
              collapsedLabel='Чат заблокирован'
              storageKey={
                selectedChatIdNumber != null
                  ? `chat-notice:${selectedChatIdNumber}:blocked`
                  : undefined
              }
              tone='error'
              title='Чат заблокирован'
            >
              {activeChatRoom.moderation_blocked_reason?.trim() ||
                'Модератор закрыл переписку. Новые сообщения отправить нельзя.'}
            </Notice>
          ) : null}
          {sendBlockedReason ? (
            <Notice
              collapsible
              collapsedLabel='Переписка недоступна'
              storageKey={
                selectedChatIdNumber != null
                  ? `chat-notice:${selectedChatIdNumber}:send`
                  : undefined
              }
              tone='warning'
            >
              {sendBlockedReason}
            </Notice>
          ) : null}

          <ChatInput
            onSendMessage={handleSendMessage}
            disabled={messagingDisabled}
            placeholder={
              activeChatRoom?.moderation_blocked
                ? 'Чат заблокирован модерацией'
                : messagingDisabled
                  ? 'Переписка недоступна'
                  : 'Напишите сообщение...'
            }
          />
          {selectedChatIdNumber != null ? (
            <button type='button' className={screen.report} onClick={() => setReportOpen(true)}>
              Пожаловаться на чат
            </button>
          ) : null}
        </div>
      ) : (
        <div className={`${classes['chat-main']} ${screen.dialog}`}>
          <div className={classes['no-chat-selected']}>
            <h3>
              {role === 'tutor'
                ? 'Выберите чат'
                : selectedBulletinId
                  ? 'Выберите репетитора'
                  : 'Выберите задачу'}
            </h3>
            <p>
              {role === 'tutor'
                ? 'Выберите чат со студентом в боковой панели'
                : selectedBulletinId
                  ? 'Выберите репетитора из списка справа'
                  : 'Откройте задачу в списке справа: там диалоги с репетиторами'}
            </p>
          </div>
        </div>
      )}

      {sidebar}
    </div>
      )}
    </div>
    </FadeIn>
  </div>
  );
};

interface ChatProps {
  useBulletinFromRoute?: boolean;
}

export const Chat: React.FC<ChatProps> = ({ useBulletinFromRoute }) => {
  const { id } = useParams();
  const routeBulletinId =
    useBulletinFromRoute ? parsePositiveInt(id) ?? undefined : undefined;

  return <ChatStudent bulletinIdFromRoute={routeBulletinId} />;
};

export default Chat;
